import React from 'react';
import { useDropzone } from 'react-dropzone';
import { FaFileAlt, FaFileExcel, FaFilePdf, FaFileVideo, FaRegTrashAlt } from 'react-icons/fa';
import { MdOutlineAttachFile } from 'react-icons/md';
import { Button, Text } from 'rizzui';
import cn from '../../../utils/classNames';

export interface FileUploadProps {
    /** Array de archivos seleccionados */
    files: File[];
    /** Callback que se ejecuta cuando cambian los archivos */
    onFilesChange: (files: File[]) => void;
    /** Título de la sección */
    title?: string;
    /** Texto del área de drop */
    dropText?: string;
    /** Texto cuando se está arrastrando */
    dragActiveText?: string;
    /** Texto de ayuda */
    helpText?: string;
    /** Número máximo de archivos permitidos */
    maxFiles?: number;
    /** Tamaño máximo por archivo en bytes */
    maxSize?: number;
    /** Tipos de archivo aceptados */
    accept?: Record<string, string[]>;
    /** Si está deshabilitado */
    disabled?: boolean;
    /** Clases CSS adicionales para el contenedor */
    className?: string;
    /** Si debe mostrar la lista de archivos */
    showFileList?: boolean;
    /** Altura máxima de la lista de archivos */
    fileListMaxHeight?: string;
    /** Si debe mostrar el tamaño de los archivos */
    showFileSize?: boolean;
    /** Si debe mostrar íconos de tipo de archivo */
    showFileIcons?: boolean;
    /** Variante del estilo */
    variant?: 'default' | 'compact' | 'minimal';
}

const FileUpload: React.FC<FileUploadProps> = ({
    files,
    onFilesChange,
    title = 'Archivos adjuntos',
    dropText = 'Arrastra archivos aquí o haz clic para seleccionar',
    dragActiveText = 'Suelta los archivos aquí...',
    helpText,
    maxFiles = 10,
    maxSize = 10485760, // 10MB por defecto
    accept,
    disabled = false,
    className,
    showFileList = true,
    fileListMaxHeight = 'max-h-40',
    showFileSize = true,
    showFileIcons = true,
    variant = 'default'
}) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop(acceptedFiles) {
            const newFiles = [...files, ...acceptedFiles];
            onFilesChange(newFiles);
        },
        multiple: maxFiles > 1,
        maxFiles,
        maxSize,
        accept,
        disabled
    });

    const removeFile = (index: number) => {
        const newFiles = files.filter((_, i) => i !== index);
        onFilesChange(newFiles);
    };

    // Función para obtener el ícono basado en el tipo de archivo
    const getFileIcon = (file: File) => {
        if (!showFileIcons) return null;

        const fileType = file.type;
        const iconProps = { size: variant === 'compact' ? 16 : 20 };

        if (fileType === 'application/pdf') {
            return <FaFilePdf className="text-red-500" {...iconProps} />;
        } else if (fileType.startsWith('image/')) {
            return <FaFileAlt className="text-blue-500" {...iconProps} />;
        } else if (fileType.startsWith('video/')) {
            return <FaFileVideo className="text-purple-500" {...iconProps} />;
        } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
            return <FaFileExcel className="text-green-500" {...iconProps} />;
        } else if (fileType.startsWith('text/') || fileType.includes('document')) {
            return <FaFileAlt className="text-gray-500" {...iconProps} />;
        } else {
            return <MdOutlineAttachFile className="text-gray-400" {...iconProps} />;
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getVariantClasses = () => {
        switch (variant) {
            case 'compact':
                return {
                    container: 'space-y-2',
                    dropzone: 'p-4',
                    icon: 'h-6 w-6',
                    text: 'text-xs',
                    helpText: 'text-xs'
                };
            case 'minimal':
                return {
                    container: 'space-y-3',
                    dropzone: 'p-3 border',
                    icon: 'h-5 w-5',
                    text: 'text-sm',
                    helpText: 'text-xs'
                };
            default:
                return {
                    container: 'space-y-4',
                    dropzone: 'p-6',
                    icon: 'h-8 w-8',
                    text: 'text-sm',
                    helpText: 'text-xs'
                };
        }
    };

    const variantClasses = getVariantClasses();
    const defaultHelpText = `Máximo ${maxFiles} archivo${maxFiles > 1 ? 's' : ''}, ${formatFileSize(maxSize)} por archivo`;

    return (
        <div className={cn(variantClasses.container, className)}>
            {title && variant !== 'minimal' && (
                <Text className="text-sm font-medium text-gray-700">{title}</Text>
            )}

            {/* Zona de arrastrar y soltar */}
            <div
                {...getRootProps()}
                className={cn(
                    'border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors',
                    variantClasses.dropzone,
                    isDragActive
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400',
                    disabled && 'opacity-50 cursor-not-allowed'
                )}
            >
                <input {...getInputProps()} />
                <MdOutlineAttachFile className={cn('mx-auto text-gray-400 mb-2', variantClasses.icon)} />
                <Text className={cn('text-gray-600', variantClasses.text)}>
                    {isDragActive ? dragActiveText : dropText}
                </Text>
                {(helpText || (!helpText && variant === 'default')) && (
                    <Text className={cn('text-gray-400 mt-1', variantClasses.helpText)}>
                        {helpText || defaultHelpText}
                    </Text>
                )}
            </div>

            {/* Lista de archivos seleccionados */}
            {showFileList && files.length > 0 && (
                <div className="space-y-2">
                    <Text className="text-sm font-medium text-gray-700">
                        Archivos seleccionados ({files.length})
                    </Text>
                    <div className={cn('overflow-y-auto grid md:grid-cols-2 gap-2', fileListMaxHeight)}>
                        {files.map((file, index) => (
                            <div
                                key={`${file.name}-${index}`}
                                className={cn(
                                    'flex items-center justify-between bg-gray-50 rounded-lg border',
                                    variant === 'compact' ? 'p-2' : 'p-3'
                                )}
                            >
                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                    {getFileIcon(file)}
                                    <div className="flex-1 min-w-0">
                                        <Text className={cn(
                                            'font-medium text-gray-900 truncate',
                                            variant === 'compact' ? 'text-xs' : 'text-sm'
                                        )}>
                                            {file.name}
                                        </Text>
                                        {showFileSize && (
                                            <Text className="text-xs text-gray-500">
                                                {formatFileSize(file.size)}
                                            </Text>
                                        )}
                                    </div>
                                </div>
                                <Button
                                    variant="text"
                                    size={variant === 'compact' ? 'sm' : 'sm'}
                                    className="text-red-500 hover:text-red-700 p-1"
                                    onClick={() => removeFile(index)}
                                    disabled={disabled}
                                >
                                    <FaRegTrashAlt size={variant === 'compact' ? 12 : 14} />
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUpload;